import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Stack,
  Card,
  Chip,
  IconButton,
  Tooltip,
  Button,
  Tabs,
  Tab,
  Link,
  Alert,
  AlertTitle,
  useMediaQuery,
  Fade
} from '@mui/material';
import {
  Error as ErrorIcon,
  Security as SecurityIcon,
  Report as SpamIcon,
  Code as HeaderIcon,
  Article as BodyIcon,
  Link as LinkIcon,
  Unsubscribe as UnsubscribeIcon,
  Info as InfoIcon,
  ContentCopy as ContentCopyIcon,
  Share as ShareIcon
} from '@mui/icons-material';
import api from '../../config/api';

// Design system
const designSystem = {
  colors: {
    primary: '#2563eb',
    secondary: '#64748b',
    success: '#059669',
    warning: '#d97706',
    error: '#dc2626',
    background: '#ffffff',
    surface: '#f8fafc',
    border: '#e2e8f0',
    text: {
      primary: '#1e293b',
      secondary: '#64748b',
      muted: '#94a3b8'
    }
  },
  spacing: {
    xs: '0.5rem',
    sm: '1rem',
    md: '1.5rem',
    lg: '2rem',
    xl: '3rem'
  },
  borderRadius: {
    sm: '0.375rem',
    md: '0.5rem',
    lg: '0.75rem',
    xl: '1rem'
  },
  shadows: {
    sm: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
    md: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
    lg: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
    xl: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)'
  }
};

// Helper functions
const getStatusColor = (status: string) => {
  if (status === 'pass') return designSystem.colors.success;
  if (status === 'warning') return designSystem.colors.warning;
  return designSystem.colors.error;
};

const isValidUrl = (url: string) => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

const extractLinks = (html: string) => {
  const linkRegex = /<a[^>]+href=["']([^"']+)["'][^>]*>/gi;
  const links: string[] = [];
  let match;
  while ((match = linkRegex.exec(html)) !== null) {
    links.push(match[1]);
  }
  return [...new Set(links)];
};

const extractUnsubscribeLinks = (html: string, listUnsubscribeHeader?: string) => {
  const unsubscribeRegex = /<a[^>]+href=["']([^"']+)["'][^>]*>([^<]*unsubscribe[^<]*|opt.?out|remove)[^<]*<\/a>/gi;
  const links: string[] = [];
  let match;
  while ((match = unsubscribeRegex.exec(html)) !== null) {
    links.push(match[1]);
  }
  // Also check for mailto: or http(s): links in List-Unsubscribe header
  if (listUnsubscribeHeader) {
    // Split by comma, extract URLs between <...> or as plain URLs
    const urlRegex = /<([^>]+)>|((mailto:|https?:)[^,\s]+)/gi;
    let m;
    while ((m = urlRegex.exec(listUnsubscribeHeader)) !== null) {
      if (m[1]) links.push(m[1]);
      else if (m[2]) links.push(m[2]);
    }
  }
  return [...new Set(links)];
};

// Utility to detect tracking pixels in HTML (1x1 images, hidden images, or known tracking domains)
const extractTrackingPixels = (html: string) => {
  const trackingPixels: { src: string; reason: string }[] = [];
  if (!html) return trackingPixels;
  // 1x1 images or hidden images
  const imgRegex = /<img[^>]+src=["']([^"']+)["'][^>]*>/gi;
  let match;
  while ((match = imgRegex.exec(html)) !== null) {
    const tag = match[0];
    const src = match[1];
    // Check for width and height attributes
    const widthAttr = tag.match(/width=["']?1["']?/i);
    const heightAttr = tag.match(/height=["']?1["']?/i);
    // Check for style attribute with width:1px and height:1px
    const styleAttr = tag.match(/style=["']([^"']*)["']/i);
    let is1x1 = false;
    if (widthAttr && heightAttr) {
      is1x1 = true;
    } else if (styleAttr) {
      const style = styleAttr[1];
      const width1 = /width\s*:\s*1px/i.test(style);
      const height1 = /height\s*:\s*1px/i.test(style);
      if (width1 && height1) is1x1 = true;
    }
    // Check for hidden by style
    const styleMatch = tag.match(/style=["'][^"']*(display\s*:\s*none|opacity\s*:\s*0|visibility\s*:\s*hidden)[^"']*["']/i);
    if (is1x1) {
      trackingPixels.push({ src, reason: '1x1 image' });
    } else if (styleMatch) {
      trackingPixels.push({ src, reason: 'Hidden by style' });
    } else {
      // Known tracking domains (add more as needed)
      const knownDomains = ['open.tracker', 'pixel.mail', 'track.mail', 'mandrillapp.com/track', 'sendgrid.net/track', 'mailgun.net/o/track'];
      if (knownDomains.some(domain => src.includes(domain))) {
        trackingPixels.push({ src, reason: 'Known tracking domain' });
      }
    }
  }
  return trackingPixels;
};

function getOverallSeverity(email: any) {
  // Prefer x-spam-score header if present and numeric
  let spamScore = 0;
  if (email.headers && email.headers['x-spam-score']) {
    const scoreVal = Array.isArray(email.headers['x-spam-score']) ? email.headers['x-spam-score'][0] : email.headers['x-spam-score'];
    const parsed = parseFloat(scoreVal);
    if (!isNaN(parsed)) spamScore = parsed;
    else if (typeof email.spam_score === 'number') spamScore = email.spam_score;
  } else if (typeof email.spam_score === 'number') {
    spamScore = email.spam_score;
  }
  const authIssues = [email.spf_status, email.dkim_status, email.dmarc_status].filter(s => s !== 'pass').length;
  if (spamScore > 5 || authIssues > 1) return 'error';
  if (spamScore > 2 || authIssues > 0) return 'warning';
  return 'success';
}

function getOverallTitle(email: any) {
  const severity = getOverallSeverity(email);
  if (severity === 'error') return 'Critical Issues Detected';
  if (severity === 'warning') return 'Minor Issues Found';
  return 'Email Analysis Passed';
}

function getOverallMessage(email: any): string {
  // Prefer x-spam-score header if present and numeric
  let spamScore = 0;
  if (email.headers && email.headers['x-spam-score']) {
    const scoreVal = Array.isArray(email.headers['x-spam-score']) ? email.headers['x-spam-score'][0] : email.headers['x-spam-score'];
    const parsed = parseFloat(scoreVal);
    if (!isNaN(parsed)) spamScore = parsed;
    else if (typeof email.spam_score === 'number') spamScore = email.spam_score;
  } else if (typeof email.spam_score === 'number') {
    spamScore = email.spam_score;
  }
  const authIssues = [email.spf_status, email.dkim_status, email.dmarc_status].filter(s => s !== 'pass').length;
  let message = '';
  if (spamScore > 5) {
    message += `High spam score (${spamScore}) detected. `;
  } else if (spamScore > 2) {
    message += `Moderate spam score (${spamScore}) detected. `;
  } else {
    message += `Good spam score (${spamScore}). `;
  }
  if (authIssues > 0) {
    message += `${authIssues} authentication issue(s) found. `;
  } else {
    message += 'All authentication checks passed. ';
  }
  if (email.spam_keywords && email.spam_keywords.length > 0) {
    message += `${email.spam_keywords.length} spam keyword(s) detected. `;
  }
  return message;
}

// --- Modern Tabbed Dashboard ---
function EmailTestResultPage() {
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<any>(null);

  const MAX_POLL_TIME = 3 * 60 * 1000; // 3 minutes
  const POLL_INTERVAL = 5000; // 5 seconds

  useEffect(() => {
    let pollTimer: NodeJS.Timeout;
    let timeTimer: NodeJS.Timeout;
    let pollCount = 0;

    const fetchData = async (isPolling = false) => {
      try {
        if (!isPolling) setLoading(true);
        // Try to get result from database (for shareable URLs)
        try {
          const response = await api.get(`/api/mail-test/iqresult/${id}`);
          if (response.data.success && response.data.data && response.data.data.emails && response.data.data.emails.length > 0) {
            setData(response.data.data);
            setLoading(false);
            return true;
          }
        } catch (dbError: any) {
          if (dbError.response?.status === 404) {
            try {
              const imapResponse = await api.get(`/api/email-test/check/${id}`);
              if (imapResponse.data.emails && imapResponse.data.emails.length > 0) {
                setData(imapResponse.data);
                setLoading(false);
                return true;
              }
              if (!isPolling) {
                setLoading(true);
                setData(imapResponse.data);
              }
              return false;
            } catch (imapError: any) {
              if (!isPolling) {
                setError(imapError.response?.data?.error || 'Failed to load email test results');
                setLoading(false);
              }
              return false;
            }
          } else {
            throw dbError;
          }
        }
        if (!isPolling) {
          setLoading(true);
          setData({ emails: [] });
        }
        return false;
      } catch (err: any) {
        if (!isPolling) {
          setError(err.response?.data?.message || err.response?.data?.error || 'Failed to load email test results');
          setLoading(false);
        }
        return false;
      }
    };

    const startPolling = async () => {
      const initialFetch = await fetchData(false);
      if (initialFetch) return;
      pollTimer = setInterval(async () => {
        pollCount++;
        try {
          const response = await api.get(`/api/email-test/check/${id}`);
          if (response.data.emails && response.data.emails.length > 0) {
            setData(response.data);
            clearInterval(pollTimer);
            clearInterval(timeTimer);
            setLoading(false);
            return;
          }
        } catch (err: any) {}
        if (pollCount * POLL_INTERVAL >= MAX_POLL_TIME) {
          clearInterval(pollTimer);
          clearInterval(timeTimer);
          setLoading(false);
          setError('No emails found after 3 minutes. Please try again or check if the email was sent correctly.');
        }
      }, POLL_INTERVAL);
      timeTimer = setInterval(() => {
        // Time elapsed logic
      }, 1000);
    };

    if (id) {
      startPolling();
    }
    return () => {
      if (pollTimer) clearInterval(pollTimer);
      if (timeTimer) clearInterval(timeTimer);
    };
  }, [id]);

  if (loading) {
    return <Box p={4}><Typography>Loading...</Typography></Box>;
  }
  if (error) {
    return <Box p={4}><Alert severity="error">{error}</Alert></Box>;
  }
  if (!data || !data.emails || data.emails.length === 0) {
    return <Box p={4}><Alert severity="info">No results found.</Alert></Box>;
  }
  return (
    <Box sx={{ minHeight: '100vh', background: designSystem.colors.surface, py: designSystem.spacing.lg }}>
      <Box display="flex" justifyContent="center">
        <Box width="100%" maxWidth={1200}>
          {data.emails.map((email: any, idx: number) => (
            <ModernTabbedDashboard key={idx} email={email} idx={idx} />
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function ModernTabbedDashboard({ email, idx }: { email: any; idx: number }) {
  const [activeTab, setActiveTab] = useState(0);
  const [headerView, setHeaderView] = useState<'processed' | 'raw' | 'raw_email'>('processed');
  const [bodyView, setBodyView] = useState<'html' | 'text' | 'raw'>('html');
  const isMobile = useMediaQuery('(max-width:600px)');

  const listUnsubHeader = email.headers?.['list-unsubscribe'] || email.headers?.['List-Unsubscribe'] || '';
  const links = extractLinks(email.body || '');
  const unsubscribeLinks = extractUnsubscribeLinks(email.body || '', listUnsubHeader);
  const brokenLinks = links.filter(link => !isValidUrl(link));
  const trackingPixels = extractTrackingPixels(email.body || '');

  // --- Summary Tab: Table of all headers except subject/body ---
  function SummaryTab() {
    const headers = email.headers || {};
    const excluded = ['subject', 'body', 'Body', 'BODY', 'content', 'Content', 'CONTENT'];
    const headerRows = Object.entries(headers)
      .filter(([key]) => !excluded.some(ex => key.toLowerCase().includes(ex)))
      .sort(([a], [b]) => a.localeCompare(b));
    return (
      <Box>
        <Typography variant="h5" fontWeight={700} sx={{ mb: 2, color: designSystem.colors.text.primary }}>
          Email Header Summary
        </Typography>
        <Box sx={{ overflowX: 'auto', borderRadius: 3, border: `1px solid ${designSystem.colors.border}`, background: designSystem.colors.surface }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'inherit' }}>
            <thead>
              <tr>
                <th style={{ padding: '12px 8px', borderBottom: `2px solid ${designSystem.colors.border}`, color: designSystem.colors.primary, background: designSystem.colors.background }}>Header</th>
                <th style={{ padding: '12px 8px', borderBottom: `2px solid ${designSystem.colors.border}`, color: designSystem.colors.primary, background: designSystem.colors.background }}>Value</th>
              </tr>
            </thead>
            <tbody>
              {headerRows.map(([key, value], idx) => (
                <tr key={idx} style={{ background: idx % 2 === 0 ? designSystem.colors.surface : designSystem.colors.background }}>
                  <td style={{ padding: '10px 8px', borderBottom: `1px solid ${designSystem.colors.border}`, fontWeight: 600, color: designSystem.colors.text.primary, verticalAlign: 'top', minWidth: 120 }}>{key}</td>
                  <td style={{ padding: '10px 8px', borderBottom: `1px solid ${designSystem.colors.border}`, color: designSystem.colors.text.secondary, wordBreak: 'break-all', whiteSpace: 'pre-wrap' }}>{String(Array.isArray(value) ? value.join('\n') : (typeof value === 'object' ? JSON.stringify(value) : value ?? ''))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      </Box>
    );
  }

  // --- Authentication Tab ---
  function AuthenticationTab() {
    let spfIp = '';
    const receivedSpf = email.headers?.['received-spf'] || email.headers?.['Received-SPF'] || '';
    const spfIpMatch = receivedSpf.match(/client-ip=([\d\.:a-fA-F]+)/);
    if (spfIpMatch) spfIp = spfIpMatch[1];
    // Improved: If not found, look for first public IP in all 'received' headers
    function isPublicIp(ip: string) {
      if (!ip) return false;
      if (ip === '127.0.0.1' || ip === '::1') return false;
      if (/^10\./.test(ip)) return false;
      if (/^192\.168\./.test(ip)) return false;
      if (/^172\.(1[6-9]|2[0-9]|3[0-1])\./.test(ip)) return false;
      if (/^fc00:/i.test(ip) || /^fe80:/i.test(ip)) return false;
      return true;
    }
    if (!spfIp && email.headers?.['received']) {
      const receivedHeaders = Array.isArray(email.headers['received']) ? email.headers['received'] : [email.headers['received']];
      for (const received of receivedHeaders) {
        // Try to extract all IPs in brackets
        const ipMatches = [...(received.matchAll(/\[([\d\.:a-fA-F]+)\]/g) || [])];
        for (const match of ipMatches) {
          const ip = match[1];
          if (isPublicIp(ip)) {
            spfIp = ip;
            break;
          }
        }
        if (spfIp) break;
      }
    }
    let dkimDomain = '';
    const dkimSig = email.headers?.['dkim-signature'] || email.headers?.['DKIM-Signature'] || '';
    const dMatch = dkimSig.match(/d=([^;\s]+)/);
    if (dMatch) dkimDomain = dMatch[1];
    let dmarcDomain = '';
    let authResults = email.headers?.['authentication-results'] || email.headers?.['Authentication-Results'] || '';
    if (typeof authResults !== 'string') authResults = '';
    const dmarcMatch = authResults.match(/dmarc=(pass|fail) \(([^\)]+)\)/i);
    if (dmarcMatch) dmarcDomain = dmarcMatch[2];
    if (!dmarcDomain && email.headers?.['dmarc-filter']) {
      const dmarcFilter = email.headers['dmarc-filter'];
      const domMatch = dmarcFilter.match(/domain ([^\s]+)/);
      if (domMatch) dmarcDomain = domMatch[1];
    }
    return (
      <Stack spacing={3}>
        <Typography variant="h5" fontWeight={800} color={designSystem.colors.text.primary}>
          Email Authentication
        </Typography>
        <Card sx={{ borderRadius: designSystem.borderRadius.xl, boxShadow: designSystem.shadows.lg, background: designSystem.colors.surface }}>
          <Box sx={{ p: designSystem.spacing.xl }}>
            <Stack spacing={4}>
              <Stack direction="row" spacing={2} alignItems="center">
                <SecurityIcon />
                <Typography variant="subtitle2" sx={{ minWidth: 80 }}>SPF</Typography>
                <Chip label={email.spf_status ? email.spf_status.toUpperCase() : 'UNKNOWN'} size="small" sx={{ background: getStatusColor(email.spf_status || 'unknown'), color: 'white', fontWeight: 700, fontSize: '0.95rem', px: 1.5, py: 0.5, letterSpacing: 0.5 }} />
              </Stack>
              {spfIp && (
                <Box sx={{ pl: 6 }}>
                  <Typography variant="body2" color={designSystem.colors.text.secondary} sx={{ fontFamily: 'monospace' }}>
                    Received IP: {spfIp}
                  </Typography>
                </Box>
              )}
              <Stack direction="row" spacing={2} alignItems="center">
                <SecurityIcon />
                <Typography variant="subtitle2" sx={{ minWidth: 80 }}>DKIM</Typography>
                <Chip label={email.dkim_status ? email.dkim_status.toUpperCase() : 'UNKNOWN'} size="small" sx={{ background: getStatusColor(email.dkim_status || 'unknown'), color: 'white', fontWeight: 700, fontSize: '0.95rem', px: 1.5, py: 0.5, letterSpacing: 0.5 }} />
              </Stack>
              {dkimDomain && (
                <Box sx={{ pl: 6 }}>
                  <Typography variant="body2" color={designSystem.colors.text.secondary} sx={{ fontFamily: 'monospace' }}>
                    Signing Domain: {dkimDomain}
                  </Typography>
                </Box>
              )}
              <Stack direction="row" spacing={2} alignItems="center">
                <SecurityIcon />
                <Typography variant="subtitle2" sx={{ minWidth: 80 }}>DMARC</Typography>
                <Chip label={email.dmarc_status ? email.dmarc_status.toUpperCase() : 'UNKNOWN'} size="small" sx={{ background: getStatusColor(email.dmarc_status || 'unknown'), color: 'white', fontWeight: 700, fontSize: '0.95rem', px: 1.5, py: 0.5, letterSpacing: 0.5 }} />
              </Stack>
              {dmarcDomain && (
                <Box sx={{ pl: 6 }}>
                  <Typography variant="body2" color={designSystem.colors.text.secondary} sx={{ fontFamily: 'monospace' }}>
                    Policy Domain: {dmarcDomain}
                  </Typography>
                </Box>
              )}
            </Stack>
          </Box>
        </Card>
      </Stack>
    );
  }

  // --- Content Tab ---
  function ContentTab() {
    const bodyContent = {
      html: email.body || 'No HTML content',
      text: email.body ? email.body.replace(/<[^>]*>/g, '') : 'No text content',
      raw: email.body || 'No content'
    };
    return (
      <Stack spacing={3}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h5" fontWeight={700} color={designSystem.colors.text.primary}>
            Email Content
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              variant={bodyView === 'html' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => setBodyView('html')}
            >
              HTML
            </Button>
            <Button
              variant={bodyView === 'text' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => setBodyView('text')}
            >
              Text
            </Button>
            <Button
              variant={bodyView === 'raw' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => setBodyView('raw')}
            >
              Raw
            </Button>
          </Stack>
        </Stack>
        <Card sx={{ borderRadius: designSystem.borderRadius.lg, boxShadow: designSystem.shadows.sm, background: designSystem.colors.surface }}>
          <Box sx={{ p: designSystem.spacing.lg }}>
            <Box sx={{ p: 2, borderRadius: designSystem.borderRadius.md, background: designSystem.colors.background, border: `1px solid ${designSystem.colors.border}`, maxHeight: 500, overflow: 'auto' }}>
              {bodyView === 'html' ? (
                <div dangerouslySetInnerHTML={{ __html: bodyContent.html }} style={{ fontFamily: 'inherit', color: designSystem.colors.text.primary }} />
              ) : (
                <pre style={{ margin: 0, fontFamily: 'monospace', fontSize: '0.875rem', color: designSystem.colors.text.primary, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {bodyContent[bodyView]}
                </pre>
              )}
            </Box>
          </Box>
        </Card>
      </Stack>
    );
  }

  // --- Links Tab ---
  function LinksTab() {
    return (
      <Stack spacing={3}>
        <Typography variant="h5" fontWeight={700} color={designSystem.colors.text.primary}>
          Link Analysis
        </Typography>
        <div style={{ marginBottom: 24 }}>
          <Card sx={{ borderRadius: designSystem.borderRadius.lg, boxShadow: designSystem.shadows.sm, background: designSystem.colors.surface }}>
            <Box sx={{ p: designSystem.spacing.lg }}>
              <Typography variant="h6" fontWeight={700} color={designSystem.colors.text.primary} sx={{ mb: 2 }}>
                All Links ({links.length})
              </Typography>
              <Stack spacing={2}>
                {links.map((link, index) => (
                  <Card key={index} sx={{ display: 'flex', alignItems: 'center', p: 2, borderRadius: designSystem.borderRadius.md, boxShadow: designSystem.shadows.sm }}>
                    <LinkIcon sx={{ color: designSystem.colors.primary, mr: 2 }} />
                    <Box sx={{ flex: 1 }}>
                      <Link
                        href={link}
                        target="_blank"
                        sx={{ color: designSystem.colors.primary, textDecoration: 'none', fontWeight: 600, wordBreak: 'break-all', '&:hover': { textDecoration: 'underline' } }}
                      >
                        {link}
                      </Link>
                    </Box>
                  </Card>
                ))}
              </Stack>
            </Box>
          </Card>
        </div>
        <div style={{ marginBottom: 24 }}>
          <Card sx={{ borderRadius: designSystem.borderRadius.lg, boxShadow: designSystem.shadows.sm, background: designSystem.colors.surface }}>
            <Box sx={{ p: designSystem.spacing.lg }}>
              <Typography variant="h6" fontWeight={700} color={designSystem.colors.text.primary} sx={{ mb: 2 }}>
                Unsubscribe Links ({unsubscribeLinks.length})
              </Typography>
              {unsubscribeLinks.length > 0 ? (
                <Stack spacing={2}>
                  {unsubscribeLinks.map((link, index) => (
                    <Card key={index} sx={{ display: 'flex', alignItems: 'center', p: 2, borderRadius: designSystem.borderRadius.md, boxShadow: designSystem.shadows.sm }}>
                      <UnsubscribeIcon sx={{ color: designSystem.colors.success, mr: 2 }} />
                      <Box sx={{ flex: 1 }}>
                        <Link
                          href={link}
                          target="_blank"
                          sx={{ color: designSystem.colors.primary, textDecoration: 'none', fontWeight: 600, wordBreak: 'break-all', '&:hover': { textDecoration: 'underline' } }}
                        >
                          {link}
                        </Link>
                      </Box>
                    </Card>
                  ))}
                </Stack>
              ) : (
                <Alert severity="warning" sx={{ borderRadius: designSystem.borderRadius.md }}>
                  No unsubscribe links found in this email.
                </Alert>
              )}
            </Box>
          </Card>
        </div>
        <div style={{ marginBottom: 24 }}>
          <Card sx={{ borderRadius: designSystem.borderRadius.lg, boxShadow: designSystem.shadows.sm, background: designSystem.colors.surface }}>
            <Box sx={{ p: designSystem.spacing.lg }}>
              <Typography variant="h6" fontWeight={700} color={designSystem.colors.text.primary} sx={{ mb: 2 }}>
                Broken Links ({brokenLinks.length})
              </Typography>
              {brokenLinks.length > 0 ? (
                <Stack spacing={2}>
                  {brokenLinks.map((link, index) => (
                    <Card key={index} sx={{ display: 'flex', alignItems: 'center', p: 2, borderRadius: designSystem.borderRadius.md, boxShadow: designSystem.shadows.sm }}>
                      <ErrorIcon sx={{ color: designSystem.colors.error, mr: 2 }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ color: designSystem.colors.error, fontWeight: 600, wordBreak: 'break-all' }}>{link}</Typography>
                        <Typography variant="caption" color={designSystem.colors.text.secondary}>Invalid URL format</Typography>
                      </Box>
                    </Card>
                  ))}
                </Stack>
              ) : (
                <Alert severity="success" sx={{ borderRadius: designSystem.borderRadius.md }}>
                  All links appear to be valid.
                </Alert>
              )}
            </Box>
          </Card>
        </div>
        <div style={{ marginBottom: 24 }}>
          <Card sx={{ borderRadius: designSystem.borderRadius.lg, boxShadow: designSystem.shadows.sm, background: designSystem.colors.surface }}>
            <Box sx={{ p: designSystem.spacing.lg }}>
              <Typography variant="h6" fontWeight={700} color={designSystem.colors.text.primary} sx={{ mb: 2 }}>
                Tracking Pixels ({trackingPixels.length})
              </Typography>
              {trackingPixels.length > 0 ? (
                <Stack spacing={2}>
                  {trackingPixels.map((pixel, index) => (
                    <Card key={index} sx={{ display: 'flex', alignItems: 'center', p: 2, borderRadius: designSystem.borderRadius.md, boxShadow: designSystem.shadows.sm }}>
                      <InfoIcon sx={{ color: designSystem.colors.warning, mr: 2 }} />
                      <Box sx={{ flex: 1 }}>
                        <Link
                          href={pixel.src}
                          target="_blank"
                          sx={{ color: designSystem.colors.primary, textDecoration: 'none', fontWeight: 600, wordBreak: 'break-all', '&:hover': { textDecoration: 'underline' } }}
                        >
                          {pixel.src}
                        </Link>
                        <Typography variant="caption" color={designSystem.colors.text.secondary}>Reason: {pixel.reason}</Typography>
                      </Box>
                    </Card>
                  ))}
                </Stack>
              ) : (
                <Alert severity="success" sx={{ borderRadius: designSystem.borderRadius.md }}>
                  No tracking pixels detected in this email.
                </Alert>
              )}
            </Box>
          </Card>
        </div>
      </Stack>
    );
  }

  // --- Headers Tab ---
  function HeadersTab() {
    const hasRawEmail = !!email.raw_email;
    const [copySuccess, setCopySuccess] = useState('');
    function copyToClipboard(text: string) {
      navigator.clipboard.writeText(text);
      setCopySuccess('Copied!');
      setTimeout(() => setCopySuccess(''), 1200);
    }
    let content = '';
    if (headerView === 'processed') {
      content = JSON.stringify(email.headers_summary, null, 2);
    } else if (headerView === 'raw') {
      content = JSON.stringify(email.headers, null, 2);
    } else if (headerView === 'raw_email') {
      content = email.raw_email || '';
    }
    const codeBlockStyle = {
      background: '#23272e',
      color: '#e3e8ee',
      borderRadius: 8,
      padding: '20px',
      fontFamily: 'monospace',
      fontSize: '1rem',
      maxHeight: 500,
      overflow: 'auto',
      marginTop: 8,
      marginBottom: 8,
      position: 'relative' as const,
      userSelect: 'text',
      whiteSpace: 'pre',
    };
    return (
      <Card sx={{ borderRadius: 6, boxShadow: '0 4px 24px 0 rgba(34,51,84,0.08)', background: '#f5f7fa', mb: 4 }}>
        <Box sx={{ p: 3, borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 2 }}>
          <HeaderIcon sx={{ fontSize: 32, color: '#223354', mr: 1 }} />
          <Typography variant="h5" fontWeight={800} sx={{ color: '#223354', letterSpacing: 1 }}>
            Email Headers
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          {copySuccess && <Chip label={copySuccess} color="success" size="small" sx={{ ml: 1 }} />}
          <Tooltip title="Copy to clipboard">
            <IconButton onClick={() => copyToClipboard(content)}>
              <ContentCopyIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ px: 3, pt: 2, bgcolor: '#fff', borderBottom: '1px solid #e2e8f0', position: 'sticky', top: 0, zIndex: 2 }}>
          <Stack direction="row" spacing={1}>
            <Button
              variant={headerView === 'processed' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => setHeaderView('processed')}
              sx={{ fontWeight: 600, color: headerView === 'processed' ? '#fff' : '#223354', bgcolor: headerView === 'processed' ? '#223354' : '#fff', borderColor: '#223354', boxShadow: 'none', textTransform: 'none' }}
            >
              Processed
            </Button>
            <Button
              variant={headerView === 'raw' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => setHeaderView('raw')}
              sx={{ fontWeight: 600, color: headerView === 'raw' ? '#fff' : '#223354', bgcolor: headerView === 'raw' ? '#223354' : '#fff', borderColor: '#223354', boxShadow: 'none', textTransform: 'none' }}
            >
              Raw JSON
            </Button>
            {hasRawEmail && (
              <Button
                variant={headerView === 'raw_email' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setHeaderView('raw_email')}
                sx={{ fontWeight: 600, color: headerView === 'raw_email' ? '#fff' : '#223354', bgcolor: headerView === 'raw_email' ? '#223354' : '#fff', borderColor: '#223354', boxShadow: 'none', textTransform: 'none' }}
              >
                Raw Email
              </Button>
            )}
          </Stack>
        </Box>
        <Box sx={{ p: 3, pt: 2 }}>
          <Box sx={codeBlockStyle}>
            <pre style={{ margin: 0 }}>{content}</pre>
          </Box>
        </Box>
        <Box sx={{ p: 3, pt: 0 }}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 2, color: '#223354' }}>
            Full Raw Header Dump
          </Typography>
          {email.headers ? (
            <Box sx={{ position: 'relative' }}>
              <Tooltip title="Copy all headers to clipboard">
                <IconButton
                  size="small"
                  sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2, color: '#e3e8ee', bgcolor: '#23272e', '&:hover': { bgcolor: '#23272e', color: '#fff' } }}
                  onClick={() => copyToClipboard(Object.entries(email.headers).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join('\n') : v}`).join('\n'))}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Box sx={{ ...codeBlockStyle, paddingTop: 4, minHeight: 180 }}>
                <pre style={{ margin: 0 }}>
                  {Object.entries(email.headers).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join('\n') : v}`).join('\n')}
                </pre>
              </Box>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">No header data available.</Typography>
          )}
        </Box>
      </Card>
    );
  }

  // --- Spam Analysis Tab ---
  function SpamAnalysisTab() {
    // Gather all spam-related headers
    const spamStatus = email.headers?.['x-spam-status'] || '';
    const spamReport = email.headers?.['x-spam-report'] || '';
    const spamKeywords = email.spam_keywords || [];
    // Parse SpamAssassin report into table rows
    function parseSpamReport(rawReport: string) {
      const spam_tests: { test: string; score: string; description: string; notes: string }[] = [];
      if (!rawReport) return spam_tests;
      const lines = rawReport.split('\n');
      for (let line of lines) {
        // Match lines that start with a score (number, possibly negative), whitespace, then a test name
        const match = line.match(/^\s*([\-\d\.]+)\s+([A-Z0-9_\-]+)\s+(.*)$/i);
        if (match) {
          const score = match[1];
          const test = match[2];
          let descriptionText = match[3] || '';
          let description = descriptionText;
          let notes = "";
          const bracketStart = descriptionText.indexOf('[');
          const bracketEnd = descriptionText.indexOf(']');
          if (bracketStart !== -1 && bracketEnd !== -1 && bracketEnd > bracketStart) {
            notes = descriptionText.substring(bracketStart + 1, bracketEnd);
            description = descriptionText.substring(0, bracketStart).trim();
          }
          spam_tests.push({ test, score, description, notes });
        }
      }
      return spam_tests;
    }
    const spamReportRows = parseSpamReport(spamReport);
    // Collect all spam-related headers
    const spamHeaders = Object.entries(email.headers || {}).filter(([k]) => k.toLowerCase().startsWith('x-spam'));
    return (
      <Box>
        <Typography variant="h5" fontWeight={700} color={designSystem.colors.text.primary} sx={{ mb: 2 }}>
          SpamAssassin Analysis
        </Typography>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={600}>Spam Headers</Typography>
          <Box component="ul" sx={{ pl: 2, mb: 1 }}>
            {spamHeaders.map(([k, v], idx) => (
              <li key={idx}>
                <b>{k}:</b> <span style={{ fontFamily: 'monospace' }}>{String(Array.isArray(v) ? v.join(' | ') : v)}</span>
              </li>
            ))}
          </Box>
        </Box>
        {spamStatus && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={600}>X-Spam-Status</Typography>
            <Box sx={{ fontFamily: 'monospace', background: '#f5f7fa', borderRadius: 2, p: 2, mb: 1 }}>{spamStatus}</Box>
          </Box>
        )}
        {spamReport && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={600}>X-Spam-Report</Typography>
            <Box sx={{ fontFamily: 'monospace', background: '#f5f7fa', borderRadius: 2, p: 2, mb: 1, whiteSpace: 'pre-wrap' }}>
              {spamReport}
            </Box>
          </Box>
        )}
        {spamReportRows.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={600}>SpamAssassin Test Table</Typography>
            <Box sx={{ overflowX: 'auto', borderRadius: 2, border: `1px solid ${designSystem.colors.border}`, background: designSystem.colors.surface }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'inherit', minWidth: 600 }}>
                <thead>
                  <tr>
                    <th style={{ padding: '8px', borderBottom: `2px solid ${designSystem.colors.border}` }}>Score</th>
                    <th style={{ padding: '8px', borderBottom: `2px solid ${designSystem.colors.border}` }}>Test</th>
                    <th style={{ padding: '8px', borderBottom: `2px solid ${designSystem.colors.border}`, maxWidth: 320, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>Description</th>
                    <th style={{ padding: '8px', borderBottom: `2px solid ${designSystem.colors.border}`, maxWidth: 320, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {spamReportRows.map((row, idx) => (
                    <tr key={idx}>
                      <td style={{ padding: '8px', borderBottom: `1px solid ${designSystem.colors.border}` }}>{row.score}</td>
                      <td style={{ padding: '8px', borderBottom: `1px solid ${designSystem.colors.border}` }}>{row.test}</td>
                      <td style={{ padding: '8px', borderBottom: `1px solid ${designSystem.colors.border}`, maxWidth: 320, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{row.description}</td>
                      <td style={{ padding: '8px', borderBottom: `1px solid ${designSystem.colors.border}`, maxWidth: 320, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{row.notes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          </Box>
        )}
        {spamKeywords.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={600}>Spam Keywords</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {spamKeywords.map((kw: string, idx: number) => (
                <Chip key={idx} label={kw} sx={{ bgcolor: designSystem.colors.warning, color: '#fff', fontWeight: 600 }} />
              ))}
            </Stack>
          </Box>
        )}
      </Box>
    );
  }

  const tabs = [
    { label: 'Summary', icon: <InfoIcon /> },
    { label: 'Authentication', icon: <SecurityIcon /> },
    { label: 'Content', icon: <BodyIcon /> },
    { label: 'Links', icon: <LinkIcon /> },
    { label: 'Headers', icon: <HeaderIcon /> },
    { label: 'Spam Analysis', icon: <SpamIcon /> },
  ];

  return (
    <Fade in={true} timeout={800 + (idx || 0) * 200}>
      <Card sx={{
        borderRadius: designSystem.borderRadius.xl,
        boxShadow: designSystem.shadows.xl,
        background: designSystem.colors.background,
        overflow: 'hidden',
        mb: 4
      }}>
        {/* Summary Verdict */}
        <Box sx={{
          p: designSystem.spacing.lg,
          background: `linear-gradient(135deg, ${designSystem.colors.primary} 0%, ${designSystem.colors.secondary} 100%)`,
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
        }}>
          <Stack direction={isMobile ? 'column' : 'row'} spacing={2} alignItems={isMobile ? 'flex-start' : 'center'}>
            <Typography variant="h4" fontWeight={700} gutterBottom>
              Email Analysis Report
            </Typography>
            <Chip label={getOverallTitle(email)} sx={{ bgcolor: getStatusColor(getOverallSeverity(email)), color: '#fff', fontWeight: 700, fontSize: '1.1rem', px: 2, py: 1.2, boxShadow: '0 2px 8px 0 rgba(34,51,84,0.10)' }} />
          </Stack>
          <Stack direction="row" spacing={2}>
            <Tooltip title="Copy Shareable URL">
              <IconButton
                sx={{ color: 'white' }}
                onClick={() => {
                  navigator.clipboard.writeText(email.global_shareable_url || window.location.href);
                }}
              >
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Share Results">
              <IconButton sx={{ color: 'white' }}>
                <ShareIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>
        {/* Overall Message */}
        <Box sx={{ px: designSystem.spacing.lg, py: designSystem.spacing.md, background: designSystem.colors.surface }}>
          <Alert severity={getOverallSeverity(email)} sx={{ borderRadius: designSystem.borderRadius.md }}>
            <AlertTitle>{getOverallTitle(email)}</AlertTitle>
            {getOverallMessage(email)}
          </Alert>
        </Box>
        {/* Navigation Tabs */}
        <Box sx={{
          borderBottom: '1.5px solid #e0e0e0',
          background: '#fff',
          px: 2,
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 64,
              '& .MuiTab-root': {
                color: '#4F5B7B',
                fontWeight: 700,
                minHeight: 64,
                fontSize: '1.08rem',
                letterSpacing: 0.2,
                borderRadius: 3,
                mx: 0.5,
                px: 2.5,
                py: 1.2,
                background: '#f7f9fb',
                transition: 'background 0.2s, color 0.2s',
                '&:hover': {
                  background: '#e3e8ee',
                  color: '#223354',
                },
                '&.Mui-selected': {
                  color: '#223354',
                  background: '#fff',
                  boxShadow: '0 2px 8px 0 rgba(34,51,84,0.06)',
                  border: '1.5px solid #4F8A8B',
                },
              },
              '& .MuiTabs-indicator': {
                background: '#4F8A8B',
                height: 3,
                borderRadius: 2,
              },
            }}
          >
            {tabs.map((tab, index) => (
              <Tab
                key={index}
                label={
                  <Stack direction="row" alignItems="center" spacing={1}>
                    {tab.icon}
                    <span>{tab.label}</span>
                  </Stack>
                }
              />
            ))}
          </Tabs>
        </Box>
        {/* Tab Content */}
        <Box sx={{ p: designSystem.spacing.lg }}>
          {activeTab === 0 && <SummaryTab />}
          {activeTab === 1 && <AuthenticationTab />}
          {activeTab === 2 && <ContentTab />}
          {activeTab === 3 && <LinksTab />}
          {activeTab === 4 && <HeadersTab />}
          {activeTab === 5 && <SpamAnalysisTab />}
        </Box>
      </Card>
    </Fade>
  );
}

export default EmailTestResultPage;
